import React, { useState, useMemo } from 'react';
import PropTypes from "prop-types";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";

/**
 * Store Report Table Component - Bảng báo cáo tổng thể cho từng store
 * Click vào store để xem chi tiết trong modal
 */
function StoreReportTable({ storeStatistics = {} }) {
  const [selectedStore, setSelectedStore] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  // Parse date DD/MM/YYYY hoặc MM/DD/YYYY (timestamp number) sang Date object để sort đúng
  const parseDateStr = (dateStr) => {
    if (typeof dateStr === 'number') return new Date(dateStr);
    const parts = String(dateStr).trim().split('/').map(Number);
    if (parts.length !== 3) return new Date(dateStr);
    // DD/MM/YYYY (ngày trước tháng trong nhiều API)
    const [d, m, y] = parts;
    if (d > 12) return new Date(y, m - 1, d); // rõ ràng là DD/MM/YYYY
    if (m > 12) return new Date(y, d - 1, m); // rõ ràng là MM/DD/YYYY
    return new Date(y, m - 1, d); // mặc định DD/MM/YYYY
  };

  // Tính toán tổng hợp cho mỗi store
  const storeSummary = useMemo(() => {
    if (!storeStatistics || Object.keys(storeStatistics).length === 0) return [];

    return Object.keys(storeStatistics).map(storeName => {
      const storeData = storeStatistics[storeName];
      const dates = Object.keys(storeData);
      
      let totalOrders = 0;
      let totalRevenue = 0;
      let totalBasecost = 0;
      let dateDetails = [];

      dates.forEach(date => {
        const dayData = storeData[date];
        const orders = dayData?.order || 0;
        const revenue = dayData?.totalPrice || 0;
        const basecost = dayData?.basecost || 0;
        
        totalOrders += orders;
        totalRevenue += revenue;
        totalBasecost += basecost;
        
        dateDetails.push({
          date,
          orders,
          revenue,
          basecost,
          profit: revenue - basecost
        });
      });

      // Sắp xếp theo ngày (đàng hoàng): dùng parseDateStr để xử lý DD/MM/YYYY
      dateDetails.sort((a, b) => parseDateStr(a.date) - parseDateStr(b.date));

      return {
        storeName,
        totalOrders,
        totalRevenue: parseFloat(Number(totalRevenue).toFixed(2)),
        totalBasecost: parseFloat(Number(totalBasecost).toFixed(2)),
        totalProfit: parseFloat(Number(totalRevenue - totalBasecost).toFixed(2)),
        dateDetails
      };
    }).sort((a, b) => b.totalRevenue - a.totalRevenue); // Sắp xếp theo revenue
  }, [storeStatistics]);

  // Format currency (Revenue, Profit fixed 2 decimal places)
  const formatCurrency = (value) => {
    const num = Number(value);
    if (isNaN(num)) return '$0.00';
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(2)}k`;
    }
    return `$${num.toFixed(2)}`;
  };

  // Format date (nhận DD/MM/YYYY từ API, hiển thị DD/MM)
  const formatDate = (dateStr) => {
    const date = parseDateStr(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return moment(date).format('DD/MM');
  };

  // Handler mở modal
  const handleOpenModal = (store) => {
    setSelectedStore(store);
    setOpenModal(true);
  };

  // Handler đóng modal
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedStore(null);
  };

  // Dữ liệu chart theo ngày cho store đang chọn (Revenue, Profit fixed 2)
  const storeChartData = useMemo(() => {
    if (!selectedStore?.dateDetails?.length) return [];
    return selectedStore.dateDetails.map((d) => ({
      name: formatDate(d.date),
      Orders: d.orders,
      Revenue: parseFloat(Number(d.revenue).toFixed(2)),
      Basecost: parseFloat(Number(d.basecost).toFixed(2)),
      Profit: parseFloat(Number(d.profit).toFixed(2)),
    }));
  }, [selectedStore?.dateDetails]);

  // Format giá trị lớn cho tooltip/axis
  const formatChartValue = (value) => {
    if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
    return value;
  };

  if (storeSummary.length === 0) {
    return (
      <Card>
        <MDBox p={2}>
          <MDTypography variant="body2" color="text" textAlign="center">
            Không có dữ liệu
          </MDTypography>
        </MDBox>
      </Card>
    );
  }

  return (
    <Card sx={{ height: "100%", border: "1px solid #e9ecef", boxShadow: "none", borderRadius: 2 }}>
      <MDBox p={1.5}>
        <MDTypography variant="subtitle2" mb={0.5} textAlign="center" fontWeight={600}>
          Báo Cáo Tổng Thể Theo Store
        </MDTypography>
        <MDTypography variant="caption" color="text" mb={1} display="block" textAlign="center">
          Click vào store để xem chi tiết
        </MDTypography>
        
        <div style={{ 
          width: '100%', 
          overflowX: 'auto',
          border: '1px solid #e0e0e0',
          borderRadius: '4px'
        }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            fontSize: '12px'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f5f5f5' }}>
                <th style={{ 
                  fontWeight: 'bold', 
                  padding: '8px 10px',
                  textAlign: 'left',
                  borderBottom: '2px solid #e0e0e0'
                }}>
                  Store
                </th>
                <th style={{ 
                  fontWeight: 'bold', 
                  padding: '8px 10px',
                  textAlign: 'right',
                  borderBottom: '2px solid #e0e0e0'
                }}>
                  Tổng Orders
                </th>
                <th style={{ 
                  fontWeight: 'bold', 
                  padding: '8px 10px',
                  textAlign: 'right',
                  color: '#2e7d32',
                  borderBottom: '2px solid #e0e0e0'
                }}>
                  Tổng Revenue
                </th>
                <th style={{ 
                  fontWeight: 'bold', 
                  padding: '8px 10px',
                  textAlign: 'right',
                  color: '#ed6c02',
                  borderBottom: '2px solid #e0e0e0'
                }}>
                  Tổng Basecost
                </th>
                <th style={{ 
                  fontWeight: 'bold', 
                  padding: '8px 10px',
                  textAlign: 'right',
                  borderBottom: '2px solid #e0e0e0'
                }}>
                  Profit
                </th>
                <th style={{ 
                  fontWeight: 'bold', 
                  padding: '8px 10px',
                  textAlign: 'right',
                  borderBottom: '2px solid #e0e0e0'
                }}>
                  Profit %
                </th>
              </tr>
            </thead>
            <tbody>
              {storeSummary.map((store, index) => {
                const profitPercent = store.totalRevenue > 0 
                  ? ((store.totalProfit / store.totalRevenue) * 100).toFixed(1)
                  : 0;

                return (
                  <tr
                    key={store.storeName}
                    onClick={() => handleOpenModal(store)}
                    style={{
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease',
                      borderBottom: '1px solid #e0e0e0',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e3f2fd';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <td style={{ 
                      fontWeight: '500',
                      padding: '8px 10px',
                      textAlign: 'left'
                    }}>
                      {store.storeName}
                    </td>
                    <td style={{ 
                      padding: '8px 10px',
                      textAlign: 'right'
                    }}>
                      {store.totalOrders.toLocaleString()}
                    </td>
                    <td style={{ 
                      color: '#2e7d32', 
                      fontWeight: '500',
                      padding: '8px 10px',
                      textAlign: 'right'
                    }}>
                      {formatCurrency(store.totalRevenue)}
                    </td>
                    <td style={{ 
                      color: '#ed6c02', 
                      fontWeight: '500',
                      padding: '8px 10px',
                      textAlign: 'right'
                    }}>
                      {formatCurrency(store.totalBasecost)}
                    </td>
                    <td style={{ 
                      fontWeight: 'bold',
                      color: store.totalProfit >= 0 ? '#2e7d32' : '#d32f2f',
                      padding: '8px 10px',
                      textAlign: 'right'
                    }}>
                      {formatCurrency(store.totalProfit)}
                    </td>
                    <td style={{
                      fontWeight: '500',
                      color: store.totalProfit >= 0 ? '#2e7d32' : '#d32f2f',
                      padding: '8px 10px',
                      textAlign: 'right'
                    }}>
                      {profitPercent}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Tổng tổng thể */}
        <MDBox mt={2} p={1.5} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <MDTypography variant="body2" fontWeight="bold">
            Tổng: {storeSummary.reduce((sum, s) => sum + s.totalOrders, 0).toLocaleString()} orders | {' '}
            {formatCurrency(storeSummary.reduce((sum, s) => sum + s.totalRevenue, 0))} revenue | {' '}
            {formatCurrency(storeSummary.reduce((sum, s) => sum + s.totalProfit, 0))} profit
          </MDTypography>
        </MDBox>
      </MDBox>

      {/* Modal chi tiết store */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <MDTypography variant="h5" fontWeight="bold">
            {selectedStore?.storeName || ''}
          </MDTypography>
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedStore && selectedStore.dateDetails && selectedStore.dateDetails.length > 0 ? (
            <>
              {/* Chart theo ngày */}
              <MDTypography variant="body2" color="text" mb={1} fontWeight="bold">
                Biểu đồ theo ngày
              </MDTypography>
              <MDBox sx={{ width: '100%', height: 280, mb: 3 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={storeChartData}
                    margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11, fill: '#666' }}
                      angle={-45}
                      textAnchor="end"
                      height={50}
                    />
                    <YAxis tick={{ fontSize: 11, fill: '#666' }} tickFormatter={formatChartValue} />
                    <Tooltip
                      formatter={(value, name) => {
                        if (['Revenue', 'Basecost', 'Profit'].includes(name)) {
                          return [Number(value).toFixed(2), name];
                        }
                        return [formatChartValue(value), name];
                      }}
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                      }}
                    />
                    <Legend wrapperStyle={{ paddingTop: '8px' }} iconType="line" />
                    <Bar dataKey="Orders" fill="#1976d2" name="Orders" radius={[4, 4, 0, 0]} />
                    <Line
                      type="monotone"
                      dataKey="Revenue"
                      stroke="#2e7d32"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4, fill: '#2e7d32', stroke: '#fff', strokeWidth: 2 }}
                      name="Revenue"
                    />
                    <Bar dataKey="Basecost" fill="#ed6c02" name="Basecost" radius={[4, 4, 0, 0]} />
                  </ComposedChart>
                </ResponsiveContainer>
              </MDBox>

              <MDTypography variant="body2" color="text" mb={2}>
                Chi tiết theo ngày
              </MDTypography>
              
              <div style={{ 
                maxHeight: '60vh', 
                overflow: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: '4px'
              }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  fontSize: '14px'
                }}>
                  <thead style={{ 
                    position: 'sticky', 
                    top: 0, 
                    zIndex: 1,
                    backgroundColor: '#f5f5f5'
                  }}>
                    <tr>
                      <th style={{ 
                        fontWeight: 'bold', 
                        padding: '10px 12px',
                        textAlign: 'left',
                        borderBottom: '2px solid #e0e0e0',
                        backgroundColor: '#f5f5f5'
                      }}>
                        Date
                      </th>
                      <th style={{ 
                        fontWeight: 'bold', 
                        padding: '10px 12px',
                        textAlign: 'right',
                        borderBottom: '2px solid #e0e0e0',
                        backgroundColor: '#f5f5f5'
                      }}>
                        Orders
                      </th>
                      <th style={{ 
                        fontWeight: 'bold', 
                        padding: '10px 12px',
                        textAlign: 'right',
                        color: '#2e7d32',
                        borderBottom: '2px solid #e0e0e0',
                        backgroundColor: '#f5f5f5'
                      }}>
                        Revenue
                      </th>
                      <th style={{ 
                        fontWeight: 'bold', 
                        padding: '10px 12px',
                        textAlign: 'right',
                        color: '#ed6c02',
                        borderBottom: '2px solid #e0e0e0',
                        backgroundColor: '#f5f5f5'
                      }}>
                        Basecost
                      </th>
                      <th style={{ 
                        fontWeight: 'bold', 
                        padding: '10px 12px',
                        textAlign: 'right',
                        borderBottom: '2px solid #e0e0e0',
                        backgroundColor: '#f5f5f5'
                      }}>
                        Profit
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedStore.dateDetails.map((detail, index) => (
                      <tr 
                        key={index}
                        style={{ 
                          backgroundColor: index % 2 === 0 ? '#fff' : '#fafafa',
                          borderBottom: '1px solid #e0e0e0',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f0f0f0';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#fff' : '#fafafa';
                        }}
                      >
                        <td style={{ padding: '10px 12px' }}>{formatDate(detail.date)}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>{detail.orders}</td>
                        <td style={{ 
                          padding: '10px 12px', 
                          textAlign: 'right',
                          color: '#2e7d32', 
                          fontWeight: '500' 
                        }}>
                          {formatCurrency(detail.revenue)}
                        </td>
                        <td style={{ 
                          padding: '10px 12px', 
                          textAlign: 'right',
                          color: '#ed6c02', 
                          fontWeight: '500' 
                        }}>
                          {formatCurrency(detail.basecost)}
                        </td>
                        <td style={{ 
                          padding: '10px 12px', 
                          textAlign: 'right',
                          color: detail.profit >= 0 ? '#2e7d32' : '#d32f2f', 
                          fontWeight: 'bold' 
                        }}>
                          {formatCurrency(detail.profit)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div style={{ 
                marginTop: '24px', 
                paddingTop: '16px', 
                borderTop: '2px solid #e0e0e0' 
              }}>
                <MDTypography variant="h6" fontWeight="bold" color="dark">
                  Tổng: {selectedStore.totalOrders} orders | {formatCurrency(selectedStore.totalRevenue)} revenue | {formatCurrency(selectedStore.totalProfit)} profit
                </MDTypography>
              </div>
            </>
          ) : (
            <MDTypography variant="body2" color="text" textAlign="center">
              Không có dữ liệu chi tiết
            </MDTypography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDBox onClick={handleCloseModal} sx={{ cursor: 'pointer', px: 2, py: 1, borderRadius: 1, '&:hover': { backgroundColor: '#f5f5f5' } }}>
            <MDTypography variant="button" color="primary">
              Đóng
            </MDTypography>
          </MDBox>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

// PropTypes validation
StoreReportTable.propTypes = {
  storeStatistics: PropTypes.object,
};

StoreReportTable.defaultProps = {
  storeStatistics: {},
};

export default StoreReportTable;
